import { Button, Dialog, DialogContent } from "@mui/material";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import { useEffect, useState } from "react";
import styles from "./styles.module.css";
import {
  buscarVersoesProjeto,
  criarVersaoProjeto,
} from "../../../services/api-planejamento-tributario.service";
import SnackBar from "../../SnackBar";
import Loading from "../../Loading";
import ModalAnalisarIcms from "../../../pages/PreviaICMS/ModalAnalisar";
import ModalNovaVersaoProjeto from "../ModalNovaVersaoProjeto";

const ModalVersoesProjeto = ({
  open,
  onClose,
  idProjeto,
  tipoProjeto = "PIS-COFINS", // "PIS-COFINS" ou "ICMS"
  contextHook, // Hook do contexto (usePreviaPisCofins ou usePreviaIcms)
  dadosEmpresa = null, // Dados da empresa (razaoSocial, cnpj, etc.)
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [openModalAnalisar, setOpenModalAnalisar] = useState(false);
  const [modalNovaVersao, setModalNovaVersao] = useState(false);
  const getIdVersao = (v) => v?.idProjetoVersao ?? 0;
  const [versoes, setVersoes] = useState([
    {
      idProjetoVersao: 0,
      descricao: "Original",
      dtCadastro: null,
      versao: null,
    },
  ]);
  const { setVersaoSelecionada, setDadosVersao } = contextHook();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Normalizar tipo para a API (converter PIS-COFINS para piscofins, ICMS para icms)
        const tipoNormalizado = tipoProjeto.toLowerCase().replace(/[/-]/g, "");

        const response = await buscarVersoesProjeto(tipoNormalizado, idProjeto);
        const data = response.versoes || [];

        // ✅ Manter a versão "Original" visual e adicionar as versões da API
        const versaoOriginal = {
          idProjetoVersao: 0,
          descricao: "Original",
          dtCadastro: null,
          versao: null,
        };
        const versoesFinais = [versaoOriginal, ...data];
        setVersoes(versoesFinais);
      } catch (error) {
        setObjSnackBar({
          type: "error",
          msg: "Erro ao carregar versões do projeto.",
        });
      } finally {
        setLoading(false);
      }
    };

    if (open && idProjeto) {
      fetchData();
    }
  }, [open, idProjeto, tipoProjeto]);

  useEffect(() => {
    if (!open) return;

    // Reset SnackBar quando modal abre
    setObjSnackBar({ type: "", msg: "" });
  }, [open]);

  const handleClose = () => {
    // Reset estados internos
    setVersoes([{ idProjetoVersao: 0, descricao: "Original" }]);
    setOpenModalAnalisar(false);
    setModalNovaVersao(false);
    setObjSnackBar({ type: "", msg: "" }); // Reset SnackBar

    // Fecha o modal
    if (typeof onClose === "function") {
      try {
        onClose();
      } catch (error) {
        console.error("Erro ao fechar modal:", error);
      }
    }
  };

  const handleCreate = async (nomeVersao) => {
    setLoadingCreate(true);

    try {
      // Criar nova versão do projeto
      const resultado = await criarVersaoProjeto({
        descricao: nomeVersao,
        idProjeto: idProjeto,
        tipoPrevia: tipoProjeto, // "PIS-COFINS" ou "ICMS"
      });

      // Fechar modal de nova versão
      setModalNovaVersao(false);

      // Verificar resposta da API
      if (resultado && resultado.sucesso) {
        // Mostrar snackbar de sucesso
        setObjSnackBar({
          type: "success",
          msg: resultado.mensagem || "Versão criada com sucesso!",
        });

        // Recarregar versões
        const tipoNormalizado = tipoProjeto.toLowerCase().replace(/[/-]/g, "");
        const response = await buscarVersoesProjeto(tipoNormalizado, idProjeto);
        setVersoes(response.versoes || []);
      } else {
        setObjSnackBar({
          type: "error",
          msg: resultado?.mensagem || "Erro ao criar a versão",
        });
      }
    } catch (error) {
      setObjSnackBar({
        type: "error",
        msg: error.response?.data?.mensagem || "Ops! Erro ao criar versão.",
      });
    } finally {
      setLoadingCreate(false);
    }
  };

  const handleSelecionarVersao = (versao) => {
    // Selecionar versão do projeto para análise
    if (!versao || !versao.idProjetoVersao) {
      setObjSnackBar({
        type: "error",
        msg: "Versão inválida.",
      });
      return;
    }

    setVersaoSelecionada(versao);
    setDadosVersao(null);
    setOpenModalAnalisar(true);
  };

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        {loading ? (
          <Loading loading={loading} />
        ) : (
          <DialogContent>
            <div className={styles.containerTitle}>
              <div className={styles.title}>
                <FormatListNumberedIcon /> Versões do Projeto
              </div>

              <Button
                variant="contained"
                onClick={() => setModalNovaVersao(true)}
                className={styles.btnAnalisar}
                size="small"
              >
                Adicionar Versão
              </Button>
            </div>
            <div className={styles.containerVersao}>
              {(Array.isArray(versoes) ? versoes : []).map((versao, index) => {
                return (
                  <div key={index} className={styles.itemVersao}>
                    <span>
                      <h4>{versao.descricao}</h4>
                      <p style={{ fontSize: ".8rem", color: "#A4A4A4" }}>
                        {versao.dtCadastro
                          ? new Date(versao.dtCadastro).toLocaleDateString(
                              "pt-BR"
                            )
                          : "-"}
                      </p>
                    </span>
                    {getIdVersao(versao) !== 0 && (
                      <Button
                        variant="outlined"
                        onClick={() => handleSelecionarVersao(versao)}
                        className={styles.btnFechar}
                        size="small"
                      >
                        Selecionar
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
            <div className={styles.containerBtn}>
              <Button
                variant="outlined"
                onClick={handleClose}
                className={styles.btnFechar}
              >
                Fechar
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>

      <ModalNovaVersaoProjeto
        open={modalNovaVersao}
        onClose={() => setModalNovaVersao(false)}
        onCreate={handleCreate}
        loading={loadingCreate}
        tipoProjeto={tipoProjeto}
        idProjeto={idProjeto}
        dadosProjeto={{
          razaoSocial: dadosEmpresa?.razaoSocial || "Empresa",
        }}
        versoesExistentes={versoes}
      />

      {/* SnackBar tradicional */}
      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />

      {/* Modal de Análise Dinâmico */}
      {tipoProjeto === "ICMS" && (
        <ModalAnalisarIcms
          open={openModalAnalisar}
          setOpen={setOpenModalAnalisar}
          isTab={false}
        />
      )}
    </>
  );
};

export default ModalVersoesProjeto;
